import moment from "moment-timezone";
import { useConfigStore } from "../Stores/Config";
import { EffectiveHoursConfig } from "./EffectiveHoursConfig";

type DayName =
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday";

const dayNameToNumber: Record<DayName, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const DATE_FORMAT = "YYYY-MM-DD";

// How far ahead the next opening is searched when closed days are set. Covers
// any real holiday break and bounds the loop.
const CLOSED_DAYS_LOOKAHEAD = 400;

const expandScheduleDays = (day: string): number[] => {
  if (day === "Weekdays") {
    return [1, 2, 3, 4, 5];
  }
  if (day === "Weekends") {
    return [6, 0];
  }
  if (day === "All days") {
    return [0, 1, 2, 3, 4, 5, 6];
  }
  return [dayNameToNumber[day as DayName]];
};

const hasUsableRow = (schedule: any) =>
  !!schedule?.day && !!schedule?.openingTime && !!schedule?.closingTime;

/**
 * The first instant after `now` inside office hours that is not on a closed
 * day. A closed day closes the whole local day, so an overnight window
 * running into an open day reopens at midnight. Mirrors the server's
 * nextOpeningAtOrAfter.
 */
const nextOpeningSkippingClosedDays = (
  openingTimes: any[],
  tz: string,
  now: moment.Moment,
  closedDates: Set<string>,
): moment.Moment | null => {
  const rows = openingTimes.filter(hasUsableRow);
  const firstDay = now.clone().tz(tz).subtract(1, "day").startOf("day");
  let best: moment.Moment | null = null;

  for (let offset = 0; offset <= CLOSED_DAYS_LOOKAHEAD; offset++) {
    const dayStart = firstDay.clone().add(offset, "days");
    if (best && dayStart.isAfter(best)) {
      break;
    }

    for (const row of rows) {
      if (!expandScheduleDays(row.day).includes(dayStart.day())) {
        continue;
      }
      const [openH, openM] = row.openingTime.split(":").map(Number);
      const [closeH, closeM] = row.closingTime.split(":").map(Number);
      const opening = dayStart.clone().set({ hour: openH, minute: openM });
      const closing = dayStart.clone().set({ hour: closeH, minute: closeM });
      if (!closing.isAfter(opening)) {
        closing.add(1, "day");
      }

      // Split at local midnight so each piece belongs to one calendar day.
      let pieceStart = opening.clone();
      while (pieceStart.isBefore(closing)) {
        const pieceEnd = moment.min(
          pieceStart.clone().add(1, "day").startOf("day"),
          closing,
        );
        if (
          pieceEnd.isAfter(now) &&
          !closedDates.has(pieceStart.format(DATE_FORMAT))
        ) {
          const candidate = moment.max(pieceStart, now.clone().tz(tz));
          if (!best || candidate.isBefore(best)) {
            best = candidate;
          }
        }
        pieceStart = pieceEnd;
      }
    }
  }

  return best ? best.clone().utc() : null;
};

/**
 * Evaluate online state.
 *
 * @param configOverride  Optional config to evaluate against. Pass a ticket's
 *                        effectiveHoursConfig (resolved server-side, includes
 *                        the assigned team's operating hours when applicable)
 *                        to render team-aware reply-time text. Omit to use the
 *                        global project config (header subtitle, etc.).
 */
export const isOnline = (configOverride?: EffectiveHoursConfig) => {
  const config = configOverride ?? useConfigStore.getState().config;

  if (config?.alwaysOnline) {
    return {
      online: true,
      nextOpeningTime: null,
      currentOpeningTime: null,
    };
  }

  if (!config.openingTimes) {
    return {
      online: false,
      nextOpeningTime: null,
      currentOpeningTime: null,
    };
  }

  // Use the configured timezone
  const tz = config?.timezone?.trim() ?? "UTC";
  const now = moment.tz(tz);
  const nowUtc = now.clone().utc();

  // Arrays to collect schedule periods and next-occurrence candidates.
  const periods = [];
  const nextCandidates = [];

  // Loop through each scheduled rule.
  for (let i = 0; i < config.openingTimes.length; i++) {
    const schedule = config.openingTimes[i];

    // Defensive: a half-configured schedule entry (no day / no openingTime /
    // no closingTime) would have crashed isOnline previously. Skip silently —
    // the row simply doesn't contribute to online evaluation.
    if (!schedule?.day || !schedule?.openingTime || !schedule?.closingTime) {
      continue;
    }

    const days = expandScheduleDays(schedule.day);

    // Process each day from the rule
    for (let j = 0; j < days.length; j++) {
      const day = days[j];

      // Parse times
      const [openH, openM] = schedule.openingTime.split(":").map(Number);
      const [closeH, closeM] = schedule.closingTime.split(":").map(Number);

      // Build candidate opening and closing times in the business’s local timezone.
      let candidateOpening = moment.tz(tz).day(day).set({
        hour: openH,
        minute: openM,
        second: 0,
        millisecond: 0,
      });
      let candidateClosing = moment.tz(tz).day(day).set({
        hour: closeH,
        minute: closeM,
        second: 0,
        millisecond: 0,
      });

      // Adjust for overnight periods (e.g. 22:00–02:00)
      if (candidateClosing.isBefore(candidateOpening)) {
        candidateClosing.add(1, "day");
      }

      // Add the period as computed to our list.
      periods.push({
        opening: candidateOpening.clone().utc(),
        closing: candidateClosing.clone().utc(),
        original: schedule,
      });

      // Compute the next occurrence candidate.
      // If the candidate opening is already in the past relative to 'now' (in local tz),
      // then add 7 days to get the upcoming occurrence.
      let nextOccurrence = candidateOpening.clone();
      let nextOccurrenceClosing = candidateClosing.clone();
      if (nextOccurrenceClosing.isBefore(now)) {
        nextOccurrence.add(7, "days");
      }

      nextCandidates.push(nextOccurrence.clone().utc());
    }
  }

  // Determine if currently online by checking if UTC now is in any period.
  let online = false;
  let currentOpeningTime = null;
  for (let period of periods) {
    if (nowUtc.isBetween(period.opening, period.closing, null, "[)")) {
      online = true;
      currentOpeningTime = period.original;
      break; // found an active period
    }
  }

  // Determine the next opening time from the candidates.
  let nextOpeningTime = null;
  const futureCandidates = nextCandidates.filter((candidate) =>
    candidate.isAfter(nowUtc),
  );
  if (futureCandidates.length > 0) {
    nextOpeningTime = futureCandidates.reduce((min, candidate) =>
      candidate.isBefore(min) ? candidate : min,
    );
  } else {
    // In case none are in the future, fall back to the minimum candidate plus 7 days.
    nextOpeningTime = moment.min(nextCandidates).add(7, "days");
  }

  // Closed days (public holidays, custom days) close the whole local day, and
  // "we're back" has to skip them.
  const closedDates = new Set<string>(config?.closedDates ?? []);
  if (closedDates.size > 0 && periods.length > 0) {
    if (online && closedDates.has(now.format(DATE_FORMAT))) {
      online = false;
      currentOpeningTime = null;
    }
    if (!online) {
      nextOpeningTime = nextOpeningSkippingClosedDays(
        config.openingTimes,
        tz,
        now,
        closedDates,
      );
    }
  }

  return {
    online,
    nextOpeningTime,
    currentOpeningTime,
  };
};
